/**
 * The subjects of the Belgian primary curriculum used by this journal,
 * each with the pastel colour it gets in the week grid.
 */
export const SUBJECTS = [
  ['Français', '#f8dfe8'],
  ['Mathématiques', '#dfeefa'],
  ['Éveil scientifique', '#e2f2e6'],
  ['Éveil historique', '#e2f2e6'],
  ['Éveil géographique', '#e2f2e6'],
  ['Formation manuelle, technique, technologique et numérique', '#fff1c9'],
  ['Formation économique et sociale', '#e2f2e6'],
  ['Éducation culturelle et artistique', '#eee5fa'],
  ['Éducation physique, bien-être et santé', '#fde4d7'],
  ['Éducation à la philosophie et à la citoyenneté', '#f4e8d9'],
  ['Cours philosophiques', '#f4e8d9'],
  ['Langues modernes', '#dff2f2'],
  ['Autre', '#e4e7ec'],
] as const satisfies readonly (readonly [string, string])[];

export type Subject = (typeof SUBJECTS)[number][0];

export const SUBJECT_NAMES: readonly string[] = SUBJECTS.map(([name]) => name);

/** Subjects backed by the curriculum, excluding the free-form activity category. */
export const CURRICULUM_SUBJECT_NAMES: readonly string[] = SUBJECT_NAMES.filter(
  (name) => name !== 'Autre',
);

export const DEFAULT_SUBJECT: Subject = 'Français';

/**
 * Curriculum domains offered when a French or mathematics lesson is edited.
 *
 * Finer grained than the report domains in `src/lib/bulletin-subjects.ts`, and
 * deliberately kept separate from them: a lesson is planned on "Conjugaison",
 * but the report only ever knows "Écrire".
 */
export const DOMAINS_BY_SUBJECT: Readonly<Record<string, readonly string[]>> = {
  Français: [
    'Grammaire',
    'Conjugaison',
    'Lire',
    'Écrire',
    'Parler',
    'Écouter',
    'Vocabulaire',
    'Orthographe',
  ],
  Mathématiques: [
    'Arithmétique et algèbre',
    'Des objets de l’espace à la géométrie',
    'Grandeurs',
    'Organisation des données et statistiques',
  ],
};

export function subjectDomains(subject: string): readonly string[] {
  return DOMAINS_BY_SUBJECT[subject] ?? [];
}

/** Colour used by lessons in the 08:30-09:00 rituals row, whatever the subject. */
export const RITUAL_COLOR = '#f1e8fb';

/** Fallback for a subject that is no longer part of the list. */
const FALLBACK_COLOR = '#edf1f5';

const COLOR_BY_SUBJECT = new Map<string, string>(SUBJECTS.map(([name, color]) => [name, color]));

export function subjectColor(subject: string | undefined | null): string {
  if (!subject) return FALLBACK_COLOR;
  return COLOR_BY_SUBJECT.get(subject) ?? FALLBACK_COLOR;
}

/** Short family name shown in the week-grid legend and the colour preview. */
const FAMILY_BY_SUBJECT = new Map<string, string>([
  ['Éveil scientifique', 'Éveil'],
  ['Éveil historique', 'Éveil'],
  ['Éveil géographique', 'Éveil'],
  ['Formation économique et sociale', 'Éveil'],
  ['Français', 'Français'],
  ['Mathématiques', 'Mathématiques'],
  ['Formation manuelle, technique, technologique et numérique', 'FMTTN'],
  ['Éducation culturelle et artistique', 'Artistique'],
  ['Éducation physique, bien-être et santé', 'EPS / bien-être'],
  ['Éducation à la philosophie et à la citoyenneté', 'Philo / citoyenneté'],
  ['Cours philosophiques', 'Cours philosophiques'],
  ['Langues modernes', 'Langues'],
  ['Autre', 'Autre'],
]);

export function subjectFamily(subject: string): string {
  return FAMILY_BY_SUBJECT.get(subject) ?? subject;
}

/** The legend strip above the week grid: one swatch per family. */
export const LEGEND_SUBJECTS: readonly { subject: string; label: string }[] = [
  { subject: 'Français', label: 'Français' },
  { subject: 'Mathématiques', label: 'Mathématiques' },
  { subject: 'Éveil scientifique', label: 'Éveil' },
  { subject: 'Formation manuelle, technique, technologique et numérique', label: 'FMTTN' },
  { subject: 'Éducation culturelle et artistique', label: 'Artistique' },
  { subject: 'Éducation physique, bien-être et santé', label: 'EPS' },
  { subject: 'Éducation à la philosophie et à la citoyenneté', label: 'Philo / citoyenneté' },
  { subject: 'Cours philosophiques', label: 'Cours philosophiques' },
  { subject: 'Langues modernes', label: 'Langues' },
  { subject: 'Autre', label: 'Autre' },
];

/**
 * Subjects renamed between versions of the app. Applied by the store migration
 * so old saved data keeps its colours and groupings.
 */
export const RENAMED_SUBJECTS: Readonly<Record<string, string>> = {
  Sciences: 'Éveil scientifique',
  'Histoire et géographie': 'Éveil historique',
};
